import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useTheme } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeStackedRecord } from 'src/store/slices/app/appSlice';

import DealDetailView from 'src/sections/deal-flow/views/deal-detail-view';
import ContactsDetailView from 'src/sections/contacts/view/contacts-detail-view';
import MyCompaniesDetailView from 'src/sections/companies/detail/companies-detail-view';

import RecordPanelFrame from './record-panel-frame';

// ----------------------------------------------------------------------

/**
 * JT1-1736: a record opened on top of the one already in view — a linked company or contact from a
 * deal overlay, or a linked deal from a contact / company profile. Opened with `openStackedRecord`.
 *
 * App-level Redux state rather than the URL: writing `?preview_type` would swap out the record
 * underneath, so the overlay the user came from would vanish (the JT1-1725 problem). It is a single
 * slot mounted once by ModalContainer, so opening another record from inside it replaces it instead
 * of piling up layers. Pinned to zIndex.modal so it sits above a record-panel overlay at that layer.
 */
export default function StackedRecordOverlay() {
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const record = useAppSelector((state) => state.app.stackedRecord);

  const { pathname, search } = useLocation();

  // The overlay belongs to the view it was opened over. Following a link changes the page, and
  // browser Back closes the record-panel overlay underneath (a query change) — either way it goes.
  useEffect(() => {
    dispatch(closeStackedRecord());
  }, [pathname, search, dispatch]);

  const onClose = () => dispatch(closeStackedRecord());

  const renderBody = () => {
    if (!record) return null;
    if (record.type === 'deal') return <DealDetailView overrideDealId={record.id} nested onDeleted={onClose} />;
    if (record.type === 'contact') return <ContactsDetailView overrideContactId={record.id} />;
    return <MyCompaniesDetailView overrideConnectionId={record.id} />;
  };

  return (
    <RecordPanelFrame
      variant="overlay"
      open={!!record}
      onClose={onClose}
      zIndex={theme.zIndex.modal}
    >
      {renderBody()}
    </RecordPanelFrame>
  );
}
